// import {Image} from '@chakra-ui/react';
import {ChevronDownIcon, ChevronUpIcon} from '@chakra-ui/icons';
import {
    Badge,
    Box,
    Center,
    Collapse,
    DarkMode,
    Drawer,
    DrawerBody,
    DrawerCloseButton,
    DrawerContent,
    DrawerFooter,
    DrawerHeader,
    DrawerOverlay,
    Flex,
    HStack,
    Heading,
    Icon,
    Image,
    Link,
    Popover,
    PopoverArrow,
    PopoverBody,
    PopoverContent,
    PopoverTrigger,
    Portal,
    Spinner,
    Stack,
    Switch,
    Text,
    Tooltip,
    VStack,
    useColorMode,
    useToken,
} from '@chakra-ui/react';
import Logo from 'assets/images/Logo.png';
import LogoIcon from 'assets/images/OXLogo2.png';
import {useAdminProfile, usePlatformBasicInfo, useProfile} from 'hooks/datahook/auth';
import {usePlatformProduct, useUserPreference} from 'hooks/datahook/settings';
import {useAuth} from 'hooks/useAuth';
import {useColorModeValueKey, useReadableTextColor} from 'hooks/useColors';
import {find, findIndex, get, isNil, set, trim} from 'lodash';
import React, {memo, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {matchPath, useMatches, useNavigate} from 'react-router-dom';
import {
    authPaths,
    navDrawerAdminConfig,
    navDrawerConfig,
    paths,
} from 'routes/path';
import {useUIStore} from 'stores/uiStore';
import tinycolor from 'tinycolor2';
import {HiMoon, HiSun, HiLogout} from 'react-icons/hi';
import CountryIcon from 'components/CountryIcon';
import {HEADER_HEIGHT, designSystemColors} from 'theme';
import {motion} from 'framer-motion';
import {useAdminPermissionSettings} from 'hooks/datahook/adminPermission';
import appConfig from 'config/app';
import {useProductAccess} from 'hooks/useProductAccess';
import { useGetApplyDetail } from 'hooks/datahook/debit';

const MotionText = motion(Text);
const MotionStack = motion(Stack);
const MotionImg = motion('img');

// Customize if reuqired
export const AppLogoArea = memo((props) => {
    const {title = 'Security', useNewShell = false} = props;
    const [sideBarOpen] = useUIStore((state) => [state.sideBarOpen]);
    const {t} = useTranslation('app');
    const _oldText = useColorModeValueKey('text');
    const textColor = useNewShell ? 'var(--text)' : _oldText;
    // G3 v2 §4.1 / v4 §10.2: the new shell renders the prototype's `.crm-logo`
    // block, which owns the divider, spacing and font. The prototype's
    // CSS-drawn `brand-mark` glyph (newui.css:84) was a design mock — it and the
    // empty `appConfig.displayName` are both dropped in favour of the real brand
    // asset, with the same img props as the old branch below. Per §0 iron rule:
    // no Chakra font-weight/font-size/padding on this HStack.
    // No paddingLeft/paddingRight either: the .crm-logo CSS owns side padding.
    if (useNewShell) {
        return (
            <HStack className="crm-logo" spacing={2}>
                <img
                    src={sideBarOpen ? appConfig.logo : appConfig.logoShort}
                    style={{
                        height: sideBarOpen ? '50px' : '40px',
                        width: sideBarOpen ? '100%' : '40px',
                        objectFit: 'contain',
                        paddingTop: 6,
                    }}
                />
            </HStack>
        );
    }
    return (
        <HStack
            spacing={2}
            width={'100%'}
            height={HEADER_HEIGHT}
            px={3}
        >
            <img
                src={sideBarOpen ? appConfig.logo : appConfig.logoShort}
                style={{
                    height: sideBarOpen ? '50px' : '40px',
                    width: sideBarOpen ? '100%' : '40px',
                    objectFit: 'contain',
                    paddingLeft: sideBarOpen ? 12 : 0,
                    paddingRight: sideBarOpen ? 12 : 0,
                    paddingTop: 6,
                    // minWidth: sideBarOpen ? '100%' : '40px',
                }}
            />
            {/* {!isOX && (
                <HStack alignItems={'flex-end'}>
                    <MotionText
                        fontSize="lg"
                        // bgGradient="linear(to-r, light.primary.400, light.primary.600)"
                        // backgroundClip="text"
                        fontWeight={'500'}
                        color={'rgb(247,201,64)'}
                        noOfLines={1}
                        animate={{
                            opacity: sideBarOpen ? 1 : 0,
                        }}
                    >
                        {appConfig?.displayName}
                    </MotionText>
                </HStack>
            )} */}
        </HStack>
    );
});

export const DrawerItem = memo((props) => {
    const {
        item,
        currentPath,
        onClick,
        isMenu,
        open,
        kycVerified,
        isSubItem,
        collapsed = false,
        useNewShell = false,
    } = props;
    const {t} = useTranslation('app');
    const navigate = useNavigate();
    const matches = useMatches();
    const isSelected = !isNil(
        find(matches, (o) => trim(o.pathname, '/') == trim(item.path, '/')),
    );
    const isDisabled = item.kycValid ? (kycVerified ? false : true) : false;

    // G3 §3.1: pre-compute the OLD shell colors at the top (hooks must run
    // unconditionally), then resolve the NEW shell values inline. The
    // activedColor / primary chain in the old shell resolves a Chakra theme
    // path like 'light.primary.500' to an actual hex via useToken before
    // tinycolor builds the rgba hover/focus/active states. The new shell
    // hardcodes the equivalent of --mint + alphas because there's no dark
    // token set yet (see G3 §3.5 TODO).
    const _oldDisabled = useColorModeValueKey('gray');
    const _oldAvailable = useColorModeValueKey('sideBarText');
    const _oldActiveKey = useColorModeValueKey('primary');
    const _oldActiveHex = useToken('colors', _oldActiveKey);
    const _oldActiveText = useReadableTextColor(_oldActiveHex);
    const _oldHover = tinycolor(_oldActiveHex).setAlpha(0.1).toRgbString();
    const _oldFocus = tinycolor(_oldActiveHex).setAlpha(0.2).toRgbString();
    const _oldActive = tinycolor(_oldActiveHex).setAlpha(0.9).toRgbString();
    const _newDisabled = 'var(--text-muted)';
    const _newAvailable = 'var(--text-muted)';
    const _newActive = 'var(--mint)';
    const _newActiveText = '#FFFFFF';
    const _newHover = 'rgba(0, 168, 232, 0.1)';
    const _newFocus = 'rgba(0, 168, 232, 0.2)';
    const _newActiveStrong = 'rgba(0, 168, 232, 0.9)';
    const disabledColor = useNewShell ? _newDisabled : _oldDisabled;
    const availableColor = useNewShell ? _newAvailable : _oldAvailable;
    const activedColor = useNewShell ? _newActive : _oldActiveHex;
    const activedTextColor = useNewShell ? _newActiveText : _oldActiveText;
    const naviItemHoverColor = useNewShell ? _newHover : _oldHover;
    const naviItemFocusColor = useNewShell ? _newFocus : _oldFocus;
    const naviItemActiveColor = useNewShell ? _newActiveStrong : _oldActive;
    // const activedTextColor = 'gray.50'
    const primaryColorValue = useNewShell ? '#00A8E8' : _oldActiveHex;
    const MenuIcon = open ? ChevronDownIcon : ChevronUpIcon;
    const [setShowMobileDrawer] = useUIStore((state) => [
        state.setShowMobileDrawer,
    ]);
    // G3 v2 §4.2: in the new shell, .crm-nav-item CSS owns padding (9/10px),
    // radius (8px), font-size (13px), font-weight (500/600), and colors
    // (text-muted / mint active). Per §0 iron rule, the HStack carrying
    // .crm-nav-item must NOT pass px/py/rounded/bg/color/fontSize/fontWeight
    // to Chakra — those are exactly the seven properties that defeat the
    // CSS via same-specificity later-in-cascade. The 'active' class is
    // toggled here; the .crm-nav-item.active rule paints mint + white.
    const navItemClass = useNewShell
        ? `crm-nav-item${isSelected ? ' active' : ''}`
        : undefined;
    return (
        <Box
            {...(useNewShell ? {} : {px: 3, rounded: 'md'})}
            flex={1}
            opacity={isDisabled ? 0.7 : 1}
        >
            <Tooltip
                openDelay={300}
                hasArrow
                label={t(item.labelKey)}
                placement="right"
            >
                <HStack
                    {...(useNewShell
                        ? {}
                        : {
                              px: 3,
                              py: 2,
                              rounded: 'md',
                          })}
                    alignItems={'center'}
                    justifyContent={'space-between'}
                    className={navItemClass}
                    {...(useNewShell
                        ? {}
                        : {
                              _hover: {
                                  bg: isSelected
                                      ? 'dark.primary.600'
                                      : naviItemHoverColor,
                                  _active: {
                                      bg: isSelected
                                          ? 'dark.primary.700'
                                          : naviItemFocusColor,
                                  },
                              },
                              bgColor: isSelected
                                  ? primaryColorValue
                                  : 'transparent',
                          })}
                    cursor={isDisabled ? 'not-allowed' : 'pointer'}
                    onClick={
                        isDisabled
                            ? null
                            : onClick
                            ? onClick
                            : () => {
                                  if (item.path) {
                                      if (item.external) {
                                          window.open(item.path, '_blank');
                                      } else {
                                          navigate(item.path);
                                      }
                                      setShowMobileDrawer(false);
                                  } else if (item.onClick) {
                                      item.onClick();
                                  }
                              }
                    }
                >
                    <HStack
                        alignItems={'center'}
                        w={'100%'}
                        // G3 v7 §13.2: 2.5 = 10px = matches .crm-nav-item gap
                        {...(useNewShell ? {spacing: 2.5} : {spacing: 3})}
                    >
                        {isSubItem && !collapsed ? (
                            <Box ml={4} />
                        ) : item.customIcon ? (
                            item.customIcon
                        ) : (
                            item.Icon && (
                                <Icon
                                    as={item.Icon}
                                    {...(useNewShell
                                        ? {className: 'crm-nav-icon'}
                                        : {
                                              boxSize: 4,
                                              color: isSelected
                                                  ? activedTextColor
                                                  : availableColor,
                                              mb: 0.5,
                                          })}
                                />
                            )
                        )}
                        <MotionText
                            {...(useNewShell
                                ? {
                                      // Color/weight come from .crm-nav-item
                                      // CSS. We only override for the
                                      // disabled state (no .disabled rule
                                      // in CSS).
                                      color: isDisabled
                                          ? 'var(--text-muted)'
                                          : undefined,
                                  }
                                : {
                                      color: isDisabled
                                          ? disabledColor
                                          : isSelected
                                          ? activedTextColor
                                          : availableColor,
                                      userSelect: 'none',
                                      fontSize: isSubItem ? 'sm' : 'md',
                                      fontWeight: isSubItem ? '300' : '400',
                                  })}
                            noOfLines={1}
                            isTruncated={true}
                            animate={{
                                opacity: collapsed && !isSubItem ? 0 : 1,
                            }}
                            initial={false}
                        >
                            <Text
                                textOverflow='ellipsis'
                                overflow= 'hidden'
                                w={'100%'}
                            >{t(item.labelKey)}</Text>
                        </MotionText>
                        {item.new && !collapsed && (
                            <Badge
                                fontWeight={'400'}
                                variant={'outline'}
                                colorScheme="linkedin"
                            >
                                {t('new')}
                            </Badge>
                        )}
                    </HStack>
                    {isMenu && !collapsed && (
                        <MenuIcon
                            boxSize={5}
                            color={
                                useNewShell
                                    ? isDisabled
                                        ? 'var(--text-muted)'
                                        : undefined
                                    : isDisabled
                                    ? disabledColor
                                    : isSelected
                                    ? activedTextColor
                                    : availableColor
                            }
                        />
                    )}
                </HStack>
            </Tooltip>
        </Box>
    );
});

export const DrawerMenuItem = memo((props) => {
    const {item, currentPath, kycVerified, collapsed = false, useNewShell = false} = props;
    // console.log(currentPath);
    const matches = useMatches();
    const isSelected = !isNil(find(matches, (o) => o.pathname === item.path));
    const [open, setOpen] = useState(isSelected);
    if (collapsed) {
        return (
            <Popover placement="right-start">
                {({isOpen, onClose}) => (
                    <>
                        <PopoverTrigger>
                            <Box>
                                <DrawerItem
                                    currentPath={currentPath}
                                    item={item}
                                    isMenu
                                    onClick={() => {}}
                                    open={isOpen}
                                    kycVerified={kycVerified}
                                    collapsed={collapsed}
                                    useNewShell={useNewShell}
                                />
                            </Box>
                        </PopoverTrigger>
                        <Portal>
                            <PopoverContent w={'200px'} placement="right-start">
                                <PopoverArrow />
                                <Stack spacing={2} py={2}>
                                    {item.paths
                                        .filter(
                                            (o) => o.hasPermission !== false,
                                        )
                                        .map((item) => {
                                            return (
                                                <DrawerItem
                                                    key={item.value}
                                                    item={item}
                                                    currentPath={currentPath}
                                                    kycVerified={kycVerified}
                                                    isSubItem
                                                    collapsed={collapsed}
                                                    useNewShell={useNewShell}
                                                />
                                            );
                                        })}
                                </Stack>
                            </PopoverContent>
                        </Portal>
                    </>
                )}
            </Popover>
        );
    }
    return (
        <Stack spacing={1}>
            <DrawerItem
                currentPath={currentPath}
                item={item}
                onClick={() => setOpen((c) => !c)}
                isMenu
                open={open}
                kycVerified={kycVerified}
                collapsed={collapsed}
                useNewShell={useNewShell}
            />
            <Collapse in={open} animateOpacity>
                <Stack spacing={1}>
                    {item.paths
                        .filter((o) => o.hasPermission !== false)
                        .map((item) => {
                            return (
                                <DrawerItem
                                    key={item.value}
                                    item={item}
                                    currentPath={currentPath}
                                    kycVerified={kycVerified}
                                    isSubItem
                                    collapsed={collapsed}
                                    useNewShell={useNewShell}
                                />
                            );
                        })}
                </Stack>
            </Collapse>
        </Stack>
    );
});

export const MobileNavDrawer = (props) => {
    const {useNewShell = false} = props;
    const [showMobileDrawer, setShowMobileDrawer] = useUIStore((state) => [
        state.showMobileDrawer,
        state.setShowMobileDrawer,
    ]);
    const {data:platformBasicInfo} = usePlatformBasicInfo()
    const _oldSideBarBg = useColorModeValueKey('sideBarBg');
    const _oldText = useColorModeValueKey('text');
    const bgColor = useNewShell ? undefined : _oldSideBarBg;
    const textColor = useNewShell ? undefined : _oldText;
    const onClose = () => setShowMobileDrawer(false);
    return (
        <Drawer
            isOpen={showMobileDrawer}
            placement="left"
            onClose={onClose}
            size={'full'}
        >
            <DrawerOverlay />
            {useNewShell ? (
                // v2.1 §8.2: Chakra's Drawer portals to document.body, which
                // means we're outside the layout's .nx-shell. We need a fresh
                // .nx root inside the drawer so the design-system tokens
                // resolve and `.nx .crm-nav-item` etc. match. `bg` is left
                // undefined so the .crm-sidebar's --panel covers the .nx
                // --ink; `border:none` strips the desktop right-edge line
                // (it's meaningless inside a full-screen drawer).
                <DrawerContent
                    px={0}
                    transition={'background-color 200ms linear'}
                >
                    <div
                        className="nx"
                        style={{
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                        }}
                    >
                        <div
                            className="crm-sidebar"
                            style={{flex: 1, overflowY: 'auto', border: 'none'}}
                        >
                            <DrawerCloseButton />
                            <DrawerHeader borderBottomWidth={0} px={3}>
                                <Text>
                                    {get(platformBasicInfo, 'companyShortName')}
                                </Text>
                            </DrawerHeader>
                            <DrawerBody px={0}>
                                <NavDrawer isInMobile useNewShell={useNewShell} />
                            </DrawerBody>
                        </div>
                    </div>
                </DrawerContent>
            ) : (
                <DrawerContent
                    px={0}
                    bg={bgColor}
                    transition={'background-color 200ms linear'}
                >
                    <DrawerCloseButton color={textColor} />
                    <DrawerHeader>
                        <Text color={textColor}>
                            {get(platformBasicInfo, 'companyShortName')}
                        </Text>
                    </DrawerHeader>
                    <DrawerBody px={0}>
                        <NavDrawer isInMobile useNewShell={useNewShell} />
                    </DrawerBody>
                </DrawerContent>
            )}
        </Drawer>
    );
};

export const NavDrawer = memo(({isInMobile = false, useNewShell = false}) => {
    const [sideBarOpen] = useUIStore((state) => [state.sideBarOpen]);
    const matches = useMatches();
    const currentPath = get(matches, '[1].pathname');
    const {isUser, roles} = useAuth();
    const {isCompany, kycRequired, kycPending, kycVerified, isIB, isStock, hasStockAccount, id, enableDebit, isRegulationOX_AU} = (
        isUser ? useProfile : useAdminProfile
    )();
    const {stockPermissions} = usePlatformProduct();
    const {data: basicInfo} = usePlatformBasicInfo();
    const {data: setting} = useUserPreference();
    const {colorMode, toggleColorMode} = useColorMode();
    const {t, i18n} = useTranslation('app');
    const {getPermission} = useAdminPermissionSettings({
        roles,
    });
    const { data: detailData } = useGetApplyDetail(id);
    const { hasAccess: hasStockAccess } = useProductAccess(isStock ? 'Share' : null);
    const languageList = [
        {
            label: 'englishConsist',
            value: 'en-US',
            county: 'US',
        },
        {
            label: 'chineseConsist',
            value: 'zh-CN',
            county: 'CN',
        },
        {
            label: 'chineseTWConsist',
            value: 'zh-TW',
            county: 'CN',
        },
        {
            label: 'koreanConsist',
            value: 'ko-KR',
            county: 'KR',
        },
    ];
    const matched = find(languageList, (o) => o.value === i18n.language);
    const config = [
        ...(isUser
            ? navDrawerConfig({
                  isIB,
                  allowDevelop: Boolean(get(setting, 'allowDevelop')),
                  isCompany,
                  platformBasicInfo: basicInfo,
                  isStock: isStock && hasStockAccess,
                  hasStockAccount,
                  isDebitCard: !!Object.keys(detailData).length && enableDebit,
                  isRegulationOX_AU
              })
            : navDrawerAdminConfig({
                getPermission,
                stockPermissions,
            })),
        ...(isInMobile
            ? [
                  {
                      labelKey: colorMode === 'light' ? 'dayMode' : 'nightMode',
                      value: 'darkMode',
                      onClick: toggleColorMode,
                      Icon: colorMode === 'light' ? HiMoon : HiSun,
                  },
                  {
                      labelKey: 'language',
                      value: 'language',
                      isMenu: true,
                      customIcon: (
                          <CountryIcon country={matched.county} size={16} />
                      ),
                      subMenu: true,
                      paths: languageList.map((item) => ({
                          labelKey: item.label,
                          value: item.value,
                          onClick: () => i18n.changeLanguage(item.value),
                      })),
                  },
                  {
                      labelKey: 'logout',
                      value: 'logout',
                      path: authPaths.logout,
                      Icon: HiLogout,
                      kycValid: false,
                  },
              ]
            : []),
    ];
    // console.log(config);
    // G3 §3.1: pre-compute the (dead-branch) spinner color so the hook runs
    // unconditionally; the ternary is just a value pick.
    const _oldGray = useColorModeValueKey('gray');
    const spinnerColor = useNewShell ? 'var(--text-muted)' : _oldGray;
    return (
        <Stack position={'relative'} overflow={'auto'}>
            {false ? (
                <Center>
                    <Spinner color={spinnerColor} />
                </Center>
            ) : (
                // G3 §3.2: crm-nav supplies the new sidebar nav-list container
                // (flex column, 2px gap). Inert on the old shell — see comment
                // on NavBar's MotionStack className.
                <Stack
                    px={0}
                    spacing={1}
                    position="relative"
                    className={useNewShell ? 'crm-nav' : undefined}
                >
                    {config
                        .filter((o) => o.hasPermission !== false)
                        .map((item) => {
                            if (item.subMenu) {
                                return (
                                    <DrawerMenuItem
                                        key={item.value}
                                        item={item}
                                        currentPath={currentPath}
                                        kycVerified={kycVerified}
                                        collapsed={
                                            isInMobile ? false : !sideBarOpen
                                        }
                                        useNewShell={useNewShell}
                                    />
                                );
                            } else {
                                return (
                                    <DrawerItem
                                        key={item.value}
                                        item={item}
                                        currentPath={currentPath}
                                        kycVerified={kycVerified}
                                        collapsed={
                                            isInMobile ? false : !sideBarOpen
                                        }
                                        useNewShell={useNewShell}
                                    />
                                );
                            }
                        })}
                </Stack>
            )}
            {process.env.REACT_APP_DEV_ENV && sideBarOpen && (
                <Stack mt={12} px={6}>
                    <Text color={'white'}>
                        Build ver: {process.env.REACT_APP_BUILD_VERSION}
                    </Text>
                </Stack>
            )}
        </Stack>
    );
});
